import { createContext, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE, useModals } from './useModal';
import { runWithNavBypass } from './navigationBypass';

const UnsavedChangesContext = createContext({
    setDirty: () => {},
    guard: (proceed) => proceed(),
});

// Confirms before actions that bypass react-router navigation discard unsaved
// edits - notably logout, which swaps the tree by nulling the user rather than
// calling the history navigator, so useNavigationGuard never sees it. A page
// calls setDirty(true) while it has edits; callers run their action through
// guard(proceed) - it prompts when dirty, otherwise proceeds immediately.
export function UnsavedChangesProvider({ children }) {
    const { showModal } = useModals();
    const { t } = useTranslation();
    const dirtyRef = useRef(false);

    const setDirty = (value) => {
        dirtyRef.current = value;
    };

    const guard = (proceed) => {
        // The confirmed action may itself navigate (e.g. logout's navigate to
        // '/'); run it under the bypass so useNavigationGuard doesn't re-prompt.
        const run = () => runWithNavBypass(proceed);
        if (!dirtyRef.current) {
            run();
            return;
        }
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('bookings.unsavedLeaveTitle'),
            content: t('bookings.unsavedLeaveMessage'),
            confirm: run,
        });
    };

    return (
        <UnsavedChangesContext.Provider value={{ setDirty, guard }}>
            {children}
        </UnsavedChangesContext.Provider>
    );
}

export function useUnsavedChanges() {
    return useContext(UnsavedChangesContext);
}
